import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchDiscoveryFeed,
  fetchDiscoveryPlaylistTracks,
} from "../services/soundcloud/discoveryService";

const EMPTY_DISCOVERY_DATA = {
  genres: [],
  chartSelections: [],
  defaultSelectionId: "",
  newReleases: [],
  recommendedCards: [],
};

function normalizeErrorMessage(error) {
  if (!error) return "Unknown error.";
  return error?.message || "Unable to load discovery feed.";
}

function useDiscoveryFeed({ enabled = true } = {}) {
  const requestIdRef = useRef(0);
  const [data, setData] = useState(EMPTY_DISCOVERY_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedGenreId, setSelectedGenreId] = useState("");
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [isLoadingPlaylist, setIsLoadingPlaylist] = useState(false);
  const [playlistError, setPlaylistError] = useState("");

  const load = useCallback(async () => {
    if (!enabled) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setIsLoading(true);
    setError("");

    try {
      const next = await fetchDiscoveryFeed();
      if (requestIdRef.current !== requestId) return;
      setData(next);
      setSelectedGenreId(next.defaultSelectionId || "");
      setPlaylistTracks([]);
      setSelectedPlaylist(null);
      setPlaylistError("");
    } catch (loadError) {
      if (requestIdRef.current !== requestId) return;
      setError(normalizeErrorMessage(loadError));
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [enabled]);

  const selectGenre = useCallback((genreId) => {
    const id = String(genreId || "");
    setSelectedGenreId(id);
    setSelectedPlaylist(null);
    setPlaylistTracks([]);
    setPlaylistError("");
  }, []);

  const openPlaylist = useCallback(async (playlistId) => {
    const id = Number(playlistId);
    if (!Number.isInteger(id) || id <= 0) return;

    setIsLoadingPlaylist(true);
    setPlaylistError("");

    try {
      const payload = await fetchDiscoveryPlaylistTracks(id);
      setSelectedPlaylist(payload.playlist);
      setPlaylistTracks(payload.tracks || []);
    } catch (loadError) {
      setPlaylistError(normalizeErrorMessage(loadError));
    } finally {
      setIsLoadingPlaylist(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, load]);

  const activeReleases = data.chartSelections.find((item) => item.id === selectedGenreId)?.playlists
    || data.newReleases
    || [];

  return {
    data,
    isLoading,
    error,
    refresh: load,
    selectedGenreId,
    activeReleases,
    selectGenre,
    openPlaylist,
    selectedPlaylist,
    playlistTracks,
    isLoadingPlaylist,
    playlistError,
  };
}

export default useDiscoveryFeed;
